import React, {
  FC,
  FormEvent,
  FormHTMLAttributes,
  KeyboardEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import FormStep from '../CreateAppFormStep/CreateAppFormStep';
import { FormRenderProps } from 'react-final-form';
import { stepsPrivateApp, stepsPublicApp } from '../../formConfig';
import { useScrollToTop, useWindowResize } from 'hooks';

import styles from './CreateAppFormContainer.module.scss';

export interface ICreateAppFormContainer
  extends Omit<FormRenderProps, 'children' | 'active'>,
    FormHTMLAttributes<HTMLFontElement> {}

const CreateAppFormContainer: FC<ICreateAppFormContainer> = ({
  values,
  className,
  submitting,
  submitError,
  handleSubmit,
  modifiedSinceLastSubmit,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [viewportHeight] = useWindowResize();
  const formRef = useRef<HTMLFormElement>(null);
  const rootClassName = classNames(styles.root, className);
  const scrollIntoViewOptions = useMemo<ScrollIntoViewOptions>(
    () => ({
      behavior: 'smooth',
      block: 'center',
    }),
    []
  );

  useScrollToTop(formRef);

  const stepsConfig = useMemo(() => {
    let steps = stepsPrivateApp;

    if (values.isPublic === 'yes') {
      steps = stepsPublicApp;
      // if (values.pricing === 'payPerUsage') steps = stepsPublicAppPPU;
    }

    return steps;
  }, [values.isPublic]);

  const lastStepIndex = useMemo<number>(() => stepsConfig.length - 1, [stepsConfig.length]);

  const handleStepChange = useCallback((value: number) => {
    setCurrentStep((prev) => prev + value);
  }, []);

  const handleScrollToElement = useCallback(
    (value: number) => {
      formRef.current?.children[currentStep + value]?.scrollIntoView(scrollIntoViewOptions);
      handleStepChange(value);
    },
    [currentStep, handleStepChange, scrollIntoViewOptions]
  );

  const handleScrollToPrevStep = useCallback(() => {
    handleScrollToElement(-1);
  }, [handleScrollToElement]);

  // TODO: merge these into 1 handler checking for e.key
  const handleEnterPress = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

  const handleTabPress = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
    }
  }, []);

  const handleFormSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      if (currentStep < lastStepIndex) {
        handleScrollToElement(1);
        return;
      }

      handleSubmit(e)?.catch((e) => console.error(e));
    },
    [currentStep, handleScrollToElement, handleSubmit, lastStepIndex]
  );

  return (
    <form
      className={rootClassName}
      onSubmit={handleFormSubmit}
      onKeyPress={handleEnterPress}
      onKeyDown={handleTabPress}
      ref={formRef}
      role='presentation'
    >
      {stepsConfig.map((step, idx) => (
        <FormStep
          className={classNames({ [styles.hidden]: currentStep !== idx })}
          maxSteps={lastStepIndex}
          stepNumber={idx}
          submitting={submitting}
          isValid={step.validate ? step.validate(values) : true}
          key={step.description}
          values={values}
          submitError={submitError}
          modifiedSinceLastSubmit={modifiedSinceLastSubmit}
          stepMaxHeight={viewportHeight - (135 + (idx < 2 ? 0 : Math.round(0.15 * viewportHeight)))} // dimensions are based on styles of components in viewport:
          // 120px = footer - 60px + header - 60px + footer shadow - 15px
          // 15% of viewport is delegated to gradient which acts as button for prev step
          {...step}
        />
      ))}
      <div
        onClick={handleScrollToPrevStep}
        className={classNames(styles.topGradient, { 'd-none': currentStep < 2 })}
        role='presentation'
      />
    </form>
  );
};

export default CreateAppFormContainer;
